import { PropertyType } from 'esa-ui-library/lib/types';
import { concat, drop, find, findIndex } from 'lodash';
import { TableHeaderType, convertToNumericEUR, flattenAll, toAmountString } from '../../../common';
import {
	categoryColumns,
	columnFormatter,
	countriesColumns,
	devicesColumns,
	gamblingReportMap,
	gamesColumns,
	noSubtotalItems,
	notMergableColums,
	partnersColums,
	providersColumns,
	subtotalLabel,
} from './GamesReport.const';
import { reportConstants } from '../../constants/reports';
import { GRSort } from './types/games-report-interfaces';
import { GRColumsFormatKey, NoSubtotalItem, SubtotalLabelItem } from './types/games-report-types';

export type GamblingListType = keyof typeof fieldsByTypeMap;

const fieldsByTypeMap = {
	partner_ids: partnersColums,
	category_ids: categoryColumns,
	game_ids: gamesColumns,
	game_provider_ids: providersColumns,
	countries: countriesColumns,
	devices: devicesColumns,
};

export const getFiltersUrl = (filters: PropertyType[]): string => {
	const urlFragments = filters.map((f) => `${f.name}=${f.value}`);
	return `?${urlFragments.join('&')}`;
};

export const replaceFirst = (list: string[], item: string): string[] => {
	const state = [...list];
	return concat(item, drop(state));
};

export const exportFormatter = (
	list: PropertyType[],
	isGrandTotal = false,
	averageRates: PropertyType[]
): PropertyType[] => {
	let state: PropertyType[] = [];

	state = list.map((r) => {
		const newRow: PropertyType = {};
		Object.keys(r).map((key) => {
			if (columnFormatter[key as GRColumsFormatKey] === 'cents') {
				const rate = isGrandTotal ? find(averageRates, ['name', r.currency])?.avg_rate : undefined;
				newRow[key] = rate === 0 ? `0 No AVG rate` : toAmountString(r[key], isGrandTotal ? 'EUR' : r.currency);
			} else {
				newRow[key] = r[key];
			}
		});
		return newRow;
	});

	return state;
};

export const getPreselectedAggregator = (list: PropertyType[]): string => {
	const saved = localStorage.getItem('gamblingReport');
	let aggregator = 'game_ids';
	if (saved !== null) {
		const columns = JSON.parse(saved);
		aggregator = find(list, ['label', columns[0]])?.value || 'game_ids';
	}

	return aggregator;
};

export const filterGamblingFieldsByEnv = (fields: TableHeaderType[]): TableHeaderType[] => {
	let result = [...fields];
	if (!process.env.REACT_APP_SITE_TITLE?.includes('Italy')) {
		result = result.filter(
			(f) =>
				f.label !== reportConstants.REPORT_BINGO_FEE &&
				f.label !== reportConstants.REPORT_JP_CONTRIBUTION &&
				f.label !== reportConstants.REPORT_JP_WIN &&
				f.label !== reportConstants.REPORT_RAKE
		);
	}

	return result;
};

export const getGamblingFields = (listType: GamblingListType): TableHeaderType[] => {
	let result: TableHeaderType[] = [];
	result = [...fieldsByTypeMap[listType], ...gamblingReportMap];

	return filterGamblingFieldsByEnv(result);
};

export const getFieldsValuesBySelectedType = (
	key: string,
	field: string,
	partners: PropertyType[],
	agreggators: PropertyType[],
	partnerId: number
): PropertyType => {
	const newFields: PropertyType = {};

	switch (key) {
		case 'partners':
			newFields.partner = find(flattenAll(partners), ['partnerId', +partnerId])?.label;
			newFields.skin_id = field;
			newFields.skin = find(flattenAll(partners), ['skinId', +field])?.label;
			break;
		case 'countries':
			newFields.country = find(agreggators, ['value', field])?.label || 'unknown';
			break;
		case 'devices':
			newFields.device = field;
			break;
		case 'providers':
			newFields.provider = find(agreggators, ['value', +field])?.label;
			break;
		case 'games':
			newFields.game_name = find(agreggators, ['value', +field])?.label;
			newFields.game_id = find(agreggators, ['value', +field])?.value;
			break;
		case 'gameCategories':
			newFields.category = find(agreggators, ['value', +field])?.label;
			break;

		default:
			break;
	}

	return newFields;
};

export const calculateTotal = (list: PropertyType[], isGrandTotal: boolean): PropertyType => {
	const sum = list.reduce((acc, val) => {
		Object.keys(val).forEach((key) => {
			if (subtotalLabel.includes(key as SubtotalLabelItem)) {
				acc[key] = 'Subtotal:';
			} else if (noSubtotalItems.includes(key as NoSubtotalItem)) {
				acc[key] = '- -';
			} else if (key === 'currency') {
				acc[key] = isGrandTotal ? 'EUR' : val[key];
			} else {
				const valueToSum = +val[key];
				const subtotal = (acc[key] || 0) + valueToSum;

				acc[key] = subtotal;
			}
		});
		return acc;
	}, {} as { [key: string]: number | string });

	return sum;
};

export const cellDataFormatter = (object: PropertyType, rates?: PropertyType[]): PropertyType => {
	const newObject = { ...object };
	Object.keys(object).map((key) => {
		const rate = rates ? find(rates, ['name', object.currency])?.avg_rate : undefined;

		switch (columnFormatter[key as GRColumsFormatKey]) {
			case 'value':
			case 'value_numeric':
				newObject[key] = object[key];
				break;
			case 'cents':
				newObject[key] = convertToNumericEUR(object[key], rate);
				break;
			case 'number':
				newObject[key] = +object[key];
				break;
			default:
				newObject[key] = object[key];
				break;
		}
	});
	return newObject;
};

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: false });
const normalizeStringValue = (value: PropertyType): string => {
	if (value === null || value === undefined) {
		return '';
	}

	return String(value).trim();
};

export const sortGamblingReportData = (sort: GRSort | null, list: PropertyType[]): PropertyType[] => {
	if (!sort) {
		return list;
	}

	const isStringValue = columnFormatter[sort.field] === 'value';
	const sortedList = list.slice().sort((a, b) => {
		if (isStringValue) {
			const aValue = normalizeStringValue(a[sort.field]);
			const bValue = normalizeStringValue(b[sort.field]);

			return collator.compare(aValue, bValue);
		}

		const numA = parseFloat(a[sort.field]);
		const numB = parseFloat(b[sort.field]);

		if (Number.isNaN(numA) && Number.isNaN(numB)) {
			return 0;
		}

		if (Number.isNaN(numA)) {
			return 1;
		}

		if (Number.isNaN(numB)) {
			return -1;
		}

		return numA - numB;
	});

	return sort.isAsc ? sortedList : sortedList.reverse();
};

export const mergeSameRowsForGrandTotal = (list: PropertyType[], serchField: string): PropertyType[] => {
	const result = list.reduce((acc: PropertyType[], val) => {
		const matchingRow = find(acc, [serchField, val[serchField]]);
		const index = findIndex(acc, [serchField, val[serchField]]);
		if (matchingRow) {
			const mergedObject: PropertyType = {};
			Object.keys(matchingRow).map((key) => {
				if (key === 'currency') {
					mergedObject.currency = 'EUR';
				} else if (notMergableColums.includes(key as NoSubtotalItem | SubtotalLabelItem)) {
					mergedObject[key] = matchingRow[key];
				} else {
					mergedObject[key] = matchingRow[key] + val[key];
				}
			});
			acc[index] = mergedObject;
		} else {
			acc.push(val);
		}
		return acc;
	}, [] as PropertyType[]);
	return result;
};
